import unescapedText from 'I18n/utils/unescapedText';
import styled from 'styled-components';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { ApprovalOperation } from 'ui-product-approvals-common';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { View } from 'foundations-components/transitional/components/View';
import { ApprovalRulesRadio } from '../ApprovalRulesRadio';
import { ApproverSelect } from '../ApproverSelect';
import { ApprovalModalMessage } from './ApprovalModalMessage';
import { ApprovalModalMessageLabelHelpI18n, ApprovalModalMessageLabelI18n } from '../i18n/ApprovalModalMessageLabelI18n';
import { ApprovalDataErrorMessage } from '../sidebar/ApprovalDataErrorMessage';
import { ApprovalDueDateInputGroup } from '../ApprovalDueDateInputGroup';
import { ApprovalTitleTextInput } from '../ApprovalTitleTextInput';
import { MAX_APPROVAL_NOTE_LENGTH } from '../../constants/requestModal';
import { LazyOrderedApprovalStepsBuilder } from '../orderedSteps/LazyOrderedApprovalStepsBuilder';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const FlushUnderLabel = styled.div.withConfig({
  displayName: "ApprovalModalBody__FlushUnderLabel"
})(({
  theme
}) => ({
  marginBlockStart: `-${theme.space['100']}`
}));
const LazyApprovalNoteEditor = Loadable({
  loader: () => import( /* webpackChunkName: "ApprovalNoteEditor" */'../common/ApprovalNoteEditor').then(module => ({
    default: module.ApprovalNoteEditor
  })),
  LoadingComponent: () => /*#__PURE__*/_jsx(LoadingSpinner, {
    layout: "centered",
    size: "small"
  }),
  ErrorComponent: ApprovalDataErrorMessage
});
export const ApprovalModalBody = ({
  alertContent,
  approvalRule,
  approvalSteps,
  approvalTitle,
  approvalType,
  approverActors,
  approvers,
  availableApprovalRules,
  canEditApprovalTitle,
  currentOperation,
  currentUserActorKey,
  dueDate,
  setDueDate,
  dueTime,
  setDueTime,
  initialNote,
  isDueDateValid,
  setIsDueDateValid,
  isNoteTooLong,
  isOrderedApprovalSteps,
  onChangeApprovers,
  onChangeApprovalRule,
  onChangeApprovalTitle,
  onChangeNote,
  onNoteOverLimit,
  onRemoveApprover,
  superAdminUserIds
}) => {
  const isRequestOperation = currentOperation === ApprovalOperation.REQUEST;
  const showApprovalMessage = !(isRequestOperation && canEditApprovalTitle);
  const noteIsRequired = currentOperation === ApprovalOperation.CANCEL || currentOperation === ApprovalOperation.REJECT || currentOperation === ApprovalOperation.REQUEST_CHANGES;
  return /*#__PURE__*/_jsxs(DialogBody, {
    maxBlockSize: "calc(100vh - 260px)",
    overflowY: "auto",
    children: [(alertContent || showApprovalMessage) && /*#__PURE__*/_jsxs(View, {
      "data-test-id": "product-approvals-approval-modal-body",
      marginBlockEnd: "300",
      as: "div",
      children: [alertContent && /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "300",
        as: "div",
        children: alertContent
      }), showApprovalMessage && /*#__PURE__*/_jsx(ApprovalModalMessage, {})]
    }), isRequestOperation && /*#__PURE__*/_jsxs(_Fragment, {
      children: [canEditApprovalTitle && /*#__PURE__*/_jsx(ApprovalTitleTextInput, {
        approvalTitle: approvalTitle,
        onChangeApprovalTitle: onChangeApprovalTitle
      }), !isOrderedApprovalSteps && /*#__PURE__*/_jsx(ApproverSelect, {
        approvers: approvers,
        value: approverActors,
        onChange: onChangeApprovers
      }), availableApprovalRules.length > 1 && /*#__PURE__*/_jsx(ApprovalRulesRadio, {
        approvalType: approvalType,
        approvalRule: approvalRule,
        onChangeApprovalRule: onChangeApprovalRule,
        isOrderedApprovalSteps: isOrderedApprovalSteps
      }), isOrderedApprovalSteps && /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.approverSteps"
        }),
        required: true,
        tooltipProps: {
          use: 'longform'
        },
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "productApprovals.ApprovalModal.approverTooltip"
        }),
        children: /*#__PURE__*/_jsx(FlushUnderLabel, {
          children: /*#__PURE__*/_jsx(LazyOrderedApprovalStepsBuilder, {
            steps: approvalSteps.steps,
            approvers: approvers,
            superAdminUserIds: superAdminUserIds,
            currentUserActorKey: currentUserActorKey,
            approvalRules: approvalRule,
            onAddStep: approvalSteps.addStep,
            onRemoveStep: approvalSteps.removeStep,
            onReorderSteps: approvalSteps.reorderSteps,
            onAddApprover: approvalSteps.addApprover,
            onRemoveApprover: onRemoveApprover,
            onMoveApprover: approvalSteps.moveApprover
          })
        })
      }), /*#__PURE__*/_jsx(ApprovalDueDateInputGroup, {
        dueDate: dueDate,
        setDueDate: setDueDate,
        dueTime: dueTime,
        setDueTime: setDueTime,
        isDueDateValid: isDueDateValid,
        setIsDueDateValid: setIsDueDateValid
      })]
    }), /*#__PURE__*/_jsx(FormControl, {
      help: /*#__PURE__*/_jsx(ApprovalModalMessageLabelHelpI18n, {
        currentOperation: currentOperation,
        approvalType: approvalType
      }),
      label: /*#__PURE__*/_jsx(ApprovalModalMessageLabelI18n, {
        currentOperation: currentOperation,
        approvalType: approvalType
      }),
      required: noteIsRequired,
      validationMessage: isNoteTooLong && /*#__PURE__*/_jsx(FormattedMessage, {
        message: "productApprovals.approvalModalNoteError.exceedsMaxLength",
        options: {
          maxLength: MAX_APPROVAL_NOTE_LENGTH
        }
      }),
      error: isNoteTooLong,
      children: /*#__PURE__*/_jsx(LazyApprovalNoteEditor, {
        "data-test-id": `product-approvals-approval-note-${currentOperation}`,
        placeholder: noteIsRequired ? unescapedText('productApprovals.approvalModalMessagePlaceholder') : unescapedText('productApprovals.approvalModalMessagePlaceholderOptional'),
        onChangeNote: onChangeNote,
        initialHtml: initialNote,
        charLimit: MAX_APPROVAL_NOTE_LENGTH,
        onOverLimit: onNoteOverLimit
      })
    })]
  });
};